import React, {useEffect, useState} from "react";
import {Alert, Button, Col, Drawer, Form, Input, InputNumber, Popconfirm, Row, Space, Switch, Table, Tag, Upload, message} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons";
import {ApiPromotionsService} from "../../../../../shared/api";
import {config} from "../../../../../shared/config/config";
import {SectionToolbar} from "../../components/SectionToolbar/SectionToolbar";
import {useCanEdit} from "../../../sectionAccess";

const PORTAL = config.API_HOST
const mediaUrl = (file) => `${config.API_HOST}/media/${String(file).split('/').map(encodeURIComponent).join('/')}`
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const dayText = (iso) => {
  if (!iso) return ''
  const [year, month, day] = String(iso).slice(0, 10).split('-').map(Number)
  return `${day} ${MONTHS[month - 1]} ${year}`
}
const today = () => new Date().toISOString().slice(0, 10)

// what agents see now: shown, off, not started or over
const stateOf = (item) => {
  if (item.shownNow) return <Tag color='green'>видят агенты</Tag>
  if (!item.active) return <Tag>выключена</Tag>
  if (item.startsAt && String(item.startsAt).slice(0, 10) > today()) return <Tag color='blue'>с {dayText(item.startsAt)}</Tag>
  return <Tag color='red'>закончилась</Tag>
}

const errorOf = (error) => error?.response?.data?.errors || {}

// one promotion: text and dates; the picture uploads at once (after the promotion is saved)
const PromotionEditor = ({item, onClose, onSaved}) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [current, setCurrent] = useState(item)
  const [uploading, setUploading] = useState(false)
  const untilMonthEnd = Form.useWatch ? Form.useWatch('untilMonthEnd', form) : false

  useEffect(() => {
    form.setFieldsValue(item
      ? {
        title: item.title,
        body: item.body || '',
        link: item.link || '',
        linkText: item.linkText || '',
        startsAt: item.startsAt ? String(item.startsAt).slice(0, 10) : '',
        endsAt: item.endsAt ? String(item.endsAt).slice(0, 10) : '',
        untilMonthEnd: item.untilMonthEnd,
        active: item.active,
        position: item.position,
      }
      : {active: true, untilMonthEnd: false, position: 0})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async () => {
    let values
    try {
      values = await form.validateFields()
    } catch (e) {
      return
    }
    const payload = {
      title: values.title,
      body: values.body || null,
      link: values.link || null,
      linkText: values.linkText || null,
      startsAt: values.startsAt || null,
      endsAt: values.endsAt || null,
      untilMonthEnd: Boolean(values.untilMonthEnd),
      active: Boolean(values.active),
      position: Number(values.position) || 0,
    }
    setSaving(true)
    try {
      const {data} = await ApiPromotionsService.save(current && current.id, payload)
      setCurrent(data)
      message.success(current ? 'Сохранено' : 'Акция добавлена. Теперь можно загрузить картинку.')
      onSaved()
    } catch (error) {
      const {message: text, field} = errorOf(error)
      if (field) form.setFields([{name: field, errors: [text]}])
      message.error(text || 'Не удалось сохранить')
    }
    setSaving(false)
  }

  const upload = async (file) => {
    setUploading(true)
    try {
      const {data} = await ApiPromotionsService.uploadImage(current.id, file)
      setCurrent(data)
      onSaved()
    } catch (error) {
      message.error(errorOf(error).message || `Не удалось загрузить ${file.name}`)
    }
    setUploading(false)
  }

  const removeImage = async () => {
    try {
      const {data} = await ApiPromotionsService.save(current.id, {image: null})
      setCurrent(data)
      onSaved()
    } catch (error) {
      message.error(errorOf(error).message || 'Не удалось убрать')
    }
  }

  return (
    <Drawer
      visible
      width={Math.min(760, window.innerWidth)}
      title={current ? current.title : 'Новая акция'}
      onClose={onClose}
      destroyOnClose
      className='complex-editor'
      footer={(
        <div className='complex-editor__footer'>
          <Button type='primary' onClick={save} loading={saving}>{current ? 'Сохранить' : 'Добавить акцию'}</Button>
          <Button onClick={onClose}>Закрыть</Button>
        </div>
      )}
    >
      <Form form={form} layout='vertical'>
        <Form.Item name='title' label='Название' rules={[{required: true, whitespace: true, message: 'Обязательное поле'}]}>
          <Input maxLength={200}/>
        </Form.Item>
        <Form.Item name='body' label='Текст' extra='Абзацы и переносы строк сохраняются как есть.'>
          <Input.TextArea autoSize={{minRows: 6, maxRows: 18}} maxLength={20000} showCount/>
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Form.Item name='link' label='Ссылка' extra='https://… или страница портала, например /apartments'>
              <Input maxLength={500}/>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name='linkText' label='Текст кнопки' extra='Пусто — «Подробнее»'>
              <Input maxLength={60}/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name='startsAt' label='Начало'>
              <Input type='date'/>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name='endsAt' label='Окончание'>
              <Input type='date' disabled={untilMonthEnd}/>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name='untilMonthEnd' label='До конца месяца' valuePropName='checked' extra='Каждый месяц срок сам сдвигается на его последний день'>
              <Switch/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name='active' label='Показывать агентам' valuePropName='checked'>
              <Switch/>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name='position' label='Порядок' extra='Меньше — выше на странице'>
              <InputNumber precision={0} style={{width: '100%'}}/>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <section className='complex-editor__group'>
        <h3>Картинка</h3>
        {!current
          ? <p className='cell-hint'>Картинку можно загрузить сразу после того, как акция будет добавлена.</p>
          : (
            <div className='complex-file'>
              <div className='complex-file__body'>
                {current.image
                  ? <a href={mediaUrl(current.image)} target='_blank' rel='noopener noreferrer'><img className='promo-image' src={mediaUrl(current.image)} alt=''/></a>
                  : <span className='cell-empty'>Нет картинки</span>}
                <Space wrap>
                  <Upload accept='.jpg,.jpeg,.png,.webp,.gif,.jfif' showUploadList={false} beforeUpload={(file) => { upload(file); return false }}>
                    <Button size='small' icon={<UploadOutlined/>} loading={uploading}>{current.image ? 'Заменить' : 'Загрузить'}</Button>
                  </Upload>
                  {current.image && (
                    <Popconfirm title='Убрать картинку?' okText='Убрать' cancelText='Нет' onConfirm={removeImage}>
                      <Button size='small' danger icon={<DeleteOutlined/>}>Убрать</Button>
                    </Popconfirm>
                  )}
                </Space>
              </div>
            </div>
          )}
      </section>
    </Drawer>
  )
}

// «Акции»: the portal page «Акции» of agents
export const PromotionsSection = () => {
  const canEdit = useCanEdit()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null)

  const load = () => {
    setLoading(true)
    ApiPromotionsService.fetchAll()
      .then(({data}) => {
        setItems(data)
        setError(null)
      })
      .catch(() => setError('Не удалось загрузить акции. Обновите страницу.'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const remove = async (id) => {
    try {
      await ApiPromotionsService.remove(id)
      message.success('Акция удалена')
      load()
    } catch (e) {
      message.error(errorOf(e).message || 'Не удалось удалить')
    }
  }

  const columns = [
    {
      title: 'Картинка',
      key: 'image',
      width: 120,
      render: (item) => (item.image
        ? <img className='complex-thumb promo-thumb' src={mediaUrl(item.image)} alt=''/>
        : <div className='complex-thumb complex-thumb--empty promo-thumb'>нет</div>),
    },
    {
      title: 'Акция',
      key: 'title',
      render: (item) => (
        <>
          <b>{item.title}</b>
          <div className='cell-hint'>
            {item.untilMonthEnd ? `до конца месяца (сейчас до ${dayText(item.deadline)})` : item.deadline ? `до ${dayText(item.deadline)}` : 'без срока'}
            {item.startsAt ? `, с ${dayText(item.startsAt)}` : ''}
          </div>
        </>
      ),
    },
    {title: 'Показ', key: 'state', render: stateOf},
    {title: 'Порядок', dataIndex: 'position', key: 'position', width: 100, align: 'right'},
    canEdit && {
      title: '',
      key: 'actions',
      width: 170,
      render: (item) => (
        <Space>
          <Button icon={<EditOutlined/>} onClick={() => setEditing(item)}>Изменить</Button>
          <Popconfirm title='Удалить акцию?' okText='Удалить' okButtonProps={{danger: true}} cancelText='Нет' onConfirm={() => remove(item.id)}>
            <Button danger icon={<DeleteOutlined/>} aria-label='Удалить'/>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(Boolean)

  return (
    <>
      <Alert
        className='section-note'
        type='info'
        showIcon
        message={<>Агенты видят включённые акции, у которых подошло начало и не прошло окончание, на <a href={`${PORTAL}/promotions`} target='_blank' rel='noopener noreferrer'>странице «Акции»</a> портала.</>}
      />
      {error && <Alert className='section-note' type='error' message={error}/>}
      <Table
        title={() => (
          <SectionToolbar count={`Всего ${items.length}`}>
            {canEdit && <Button type='primary' icon={<PlusOutlined/>} onClick={() => setEditing('new')}>Добавить акцию</Button>}
          </SectionToolbar>
        )}
        columns={columns}
        dataSource={items}
        rowKey='id'
        loading={loading}
        pagination={false}
        locale={{emptyText: 'Акций нет'}}
        sticky
        scroll={{x: 800}}
      />
      {editing && (
        <PromotionEditor
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </>
  )
}
